<template>
  <aside v-if="errors" class="rui-errors">
    <!-- Single error message -->
    <div v-if="errors.error">{{ errors.error }}</div>

    <!-- Direct error message -->
    <div v-else-if="typeof errors === 'string'">{{ errors }}</div>

    <!-- Exception -->
    <div v-else-if="isException">{{ errors.message }}</div>

    <!-- List of failing fields -->
    <template v-else>
      <h2 v-if="hasTitle">{{ $t('rui.form_errors.has_errors') }}</h2>
      <ul>
        <li v-for="(messages, field) in errors" :key="field">
          <strong>{{ fieldName(field) }}</strong>
          <template v-if="messages.length === 1">{{ messages[0] }}</template>
          <template v-else>
            <ul>
              <li v-for="message in messages" :key="message"> {{ message }}</li>
            </ul>
          </template>
        </li>
      </ul>
    </template>

    <template v-if="$slots.default">
      <!-- @slot Additional content or actions -->
      <slot />
    </template>
  </aside>
</template>

<script>
/**
 * This component exists to ease the display of form errors from a Rails API.
 *
 * For this component to work with fields-related errors, a bit of configuration has to be made:
 * - `vue-i18n` must be used
 * - `i18nObjectAttributesPath` _should_ be defined, as long as a translation object for model attributes
 * - the `rui.generic.definition` translation string must exist
 *
 * @preset Simple { "props": { "errors": { "error": "A single error. The key has to be 'error'." } } }
 * @preset Object { "props": { "errors": { "title": ["is empty"], "content": ["is meaningless", "is boring" ] } } }
 * @propsGroup Display [hasTitle]
 * @propsGroup I18n    [model]
 * @propsGroup Content [errors]
 */
export default {
  name: 'RuiErrors',
  props: {
    /**
     * Error object, like
     * - `{field: ['message1', 'message2'], field2: ...}`
     * - or `{error: 'message'}` for single errors
     */
    errors: { required: false, type: Object, default: null },
    /**
     * Model name, to translate the attributes.
     *
     * Check `i18nObjectAttributesPath` in library options
     */
    model: { required: false, type: String, default: null },
    /**
     * Whether to display a message at the top of the errors
     */
    hasTitle: { required: false, type: Boolean, default: true },
  },
  computed: {
    isException () { return this.errors instanceof Error },
  },
  methods: {
    fieldName (field) {
      if (this.model) field = this.$t(`${this.$_ruiI18nObjectAttributesPath}.${this.model}.${field}`)

      return this.$t('rui.generic.definition', { term: field })
    },
  },
}
</script>
